import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for enhanced visual appeal
const StyledHero = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
}));

const StyledSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0),
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    borderRadius: '16px',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-8px)',
    },
}));

const GradientText = styled(Typography)(({ theme }) => ({
    background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontWeight: 'bold',
}));

const LogoImage = styled('img')(({ theme }) => ({
    maxHeight: '120px',
    width: 'auto',
    marginBottom: theme.spacing(3),
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

export default function AboutUs() {
    return (
        <Box>
            {/* Hero Section */}
            <StyledHero>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <LogoImage 
                            src="https://glamourradiance.com/wp-content/uploads/2024/12/Abstract-Thank-You-for-Your-Order-Card.png"
                            alt="Glamourr & Radiance Logo"
                        />
                        <GradientText variant="h2" component="h1" gutterBottom>
                            Glamourr & Radiance
                        </GradientText>
                        <Typography 
                            variant="h5" 
                            color="text.secondary" 
                            sx={{ 
                                maxWidth: '800px', 
                                mx: 'auto',
                                lineHeight: 1.6,
                                fontWeight: 300
                            }}
                        >
                            Discover the story of Glamourr & Radiance, where luxury meets innovation 
                            to bring out the best in your skin.
                        </Typography>
                    </Box>
                </Container>
            </StyledHero>

            {/* Main Story Section */}
            <StyledSection>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={8} mx="auto">
                            <Typography 
                                variant="h4" 
                                component="h2" 
                                gutterBottom 
                                sx={{ 
                                    textAlign: 'center',
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    mb: 4
                                }}
                            >
                                Our Story
                            </Typography>
                            <Typography 
                                variant="body1" 
                                paragraph 
                                sx={{ 
                                    fontSize: '1.1rem',
                                    lineHeight: 1.8,
                                    color: 'text.primary',
                                    textAlign: 'justify'
                                }}
                            >
                                At Glamourr & Radiance, we redefine beauty and self-care by combining the 
                                finest ingredients with innovative skincare technology. From luxurious day 
                                creams to elegant body mists, every product is thoughtfully crafted to 
                                nourish, protect, and enhance your natural beauty.
                            </Typography>
                            <Typography 
                                variant="body1" 
                                paragraph 
                                sx={{ 
                                    fontSize: '1.1rem',
                                    lineHeight: 1.8,
                                    color: 'text.primary',
                                    textAlign: 'justify'
                                }}
                            >
                                Our journey began in Panchkula with a vision to create products that 
                                empower confidence and radiance. Today, Glamourr & Radiance is recognized 
                                for its commitment to quality, innovation, and sustainability.
                            </Typography>
                            <Typography 
                                variant="body1" 
                                paragraph 
                                sx={{ 
                                    fontSize: '1.1rem',
                                    lineHeight: 1.8,
                                    color: 'text.primary',
                                    textAlign: 'justify'
                                }}
                            >
                                Explore our luxurious skincare solutions and join a community that 
                                celebrates beauty with confidence.
                            </Typography>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            <Divider sx={{ mx: 4 }} />

            {/* Vision and Values Section */}
            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {/* Vision Card */}
                        <Grid item xs={12} md={6}>
                            <StyledCard>
                                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                                    <Typography 
                                        variant="h4" 
                                        component="h3" 
                                        gutterBottom 
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            textAlign: 'center',
                                            mb: 3
                                        }}
                                    >
                                        Our Vision
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            fontSize: '1.1rem',
                                            lineHeight: 1.7,
                                            color: 'text.primary',
                                            textAlign: 'justify'
                                        }}
                                    >
                                        Our vision is to redefine the beauty industry by promoting confidence 
                                        and radiance through skincare. We strive to be more than a brand—Glamour 
                                        & Radiance is a movement towards embracing your unique beauty and enhancing 
                                        it with innovative, luxurious products.
                                    </Typography>
                                </CardContent>
                            </StyledCard>
                        </Grid>

                        {/* Sustainability Card */}
                        <Grid item xs={12} md={6}>
                            <StyledCard>
                                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                                    <Typography 
                                        variant="h4" 
                                        component="h3" 
                                        gutterBottom 
                                        sx={{ 
                                            color: 'success.main',
                                            fontWeight: 600,
                                            textAlign: 'center',
                                            mb: 3
                                        }}
                                    >
                                        Our Commitment
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            fontSize: '1.1rem',
                                            lineHeight: 1.7,
                                            color: 'text.primary',
                                            textAlign: 'justify'
                                        }}
                                    >
                                        We care not only for your skin but also for the planet. That's why all 
                                        our products are cruelty-free and crafted using sustainable practices. 
                                        Your beauty journey should never come at the expense of our environment.
                                    </Typography>
                                </CardContent>
                            </StyledCard>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            {/* Call to Action Section */}
            <StyledSection>
                <Container maxWidth="md">
                    <Box 
                        sx={{ 
                            textAlign: 'center',
                            p: 6,
                            backgroundColor: 'primary.50',
                            borderRadius: '24px',
                            border: '1px solid',
                            borderColor: 'primary.100'
                        }}
                    >
                        <Typography 
                            variant="h4" 
                            component="h3" 
                            gutterBottom 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            Join Our Beauty Movement
                        </Typography>
                        <Typography 
                            variant="h6" 
                            color="text.secondary" 
                            sx={{ 
                                fontWeight: 300,
                                lineHeight: 1.6
                            }}
                        >
                            Experience the perfect blend of luxury and innovation. 
                            Discover products that celebrate your unique beauty.
                        </Typography>
                    </Box>
                </Container>
            </StyledSection>
        </Box>
    );
}